import type { ScheduleTemplateItem, ScheduleTemplateLink } from '@/lib/scheduleUtils';

export interface TemplateCsvRow {
  sort_order: number;
  title: string;
  stage_name: string;
  duration_days: number;
  start_offset_days: string;
  dependency_type: string;
  predecessor_sort_order: string;
  predecessor_title: string;
  gantt_color: string;
  is_included: boolean;
  default_task_no: string;
  notes: string;
}

function escapeCsvCell(value: string | number | boolean): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildScheduleTemplateCsv(
  items: ScheduleTemplateItem[],
  links: ScheduleTemplateLink[],
  itemIdToSort: Map<string, number>,
  itemIdToTitle: Map<string, string>
): string {
  const linkBySuccessor = new Map<string, ScheduleTemplateLink>();
  for (const link of Array.isArray(links) ? links : []) {
    if (link?.successor_item_id) {
      linkBySuccessor.set(link.successor_item_id, link);
    }
  }

  const safeItems = Array.isArray(items) ? items : [];
  const rows: TemplateCsvRow[] = safeItems.map((item) => {
    const itemId = item.id ?? '';
    const link = itemId ? linkBySuccessor.get(itemId) : undefined;
    const predSort = link ? itemIdToSort.get(link.predecessor_item_id) : undefined;
    const predTitle = link ? itemIdToTitle.get(link.predecessor_item_id) : undefined;
    return {
      sort_order: item.sort_order ?? 0,
      title: item.title ?? '',
      stage_name: item.phase ?? '',
      duration_days: item.default_duration_days ?? 1,
      start_offset_days: '',
      dependency_type: link?.link_type ?? '',
      predecessor_sort_order: predSort != null ? String(predSort) : '',
      predecessor_title: predTitle ?? '',
      gantt_color: '',
      is_included: item.default_is_included !== false,
      default_task_no: item.default_task_no != null ? String(item.default_task_no) : '',
      notes: item.notes ?? '',
    };
  });

  const headers: (keyof TemplateCsvRow)[] = [
    'sort_order',
    'title',
    'stage_name',
    'duration_days',
    'start_offset_days',
    'dependency_type',
    'predecessor_sort_order',
    'predecessor_title',
    'gantt_color',
    'is_included',
    'default_task_no',
    'notes',
  ];

  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escapeCsvCell(row[h])).join(',')),
  ];
  return lines.join('\r\n');
}

export function downloadCsvFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
